/**
 * Mapping between Zero Trust Architecture capabilities and CISA ZTMM functions.
 *
 * This is the bridge that lets the two pages act as views of the same data:
 * - The ZT Architecture page is the source of truth for `{vendors, maturity}` per capability.
 * - The CISA Maturity Model derives a level for each CISA function from those capabilities.
 *
 * Authored for the Identity and Networks pillars only (workshop hot spots).
 * The Devices, Applications, and Data pillars are not yet mapped — their CISA
 * cells will show "No tech-stack signal" until added here.
 *
 * Capability IDs come from `PILLARS` in ZeroTrustArchitecture.js (line ~395).
 * CISA function codes come from `CISA_FUNCTIONS` in cisaMaturityModel.js.
 */

/**
 * Forward map: a ZT capability contributes to one or more CISA functions.
 * Many ZT capabilities feed multiple CISA functions (e.g., PAM is both
 * authentication and access management).
 */
export const ZT_CAPABILITY_TO_CISA = {
  // Identity pillar
  idp:           ['ID_STORES'],
  mfa:           ['ID_AUTH'],
  iga:           ['ID_ACCESS'],
  pam:           ['ID_AUTH', 'ID_ACCESS'],
  'machine-id':  ['ID_STORES'],
  conditional:   ['ID_RISK'],

  // Networks pillar
  ztna:          ['NET_SEG'],
  microseg:      ['NET_SEG'],
  dns:           ['NET_TRAFFIC'],
  sdwan:         ['NET_RESIL', 'NET_TRAFFIC'],
  nta:           ['NET_TRAFFIC'],
  lateral:       ['NET_SEG']
};

/**
 * Inverse map (computed once): which ZT capabilities feed a given CISA function.
 * { CISA_FUNCTION_CODE: [zt_capability_id, ...] }
 */
export const CISA_FUNCTION_TO_ZT = (() => {
  const inverse = {};
  for (const [capId, functions] of Object.entries(ZT_CAPABILITY_TO_CISA)) {
    for (const fnCode of functions) {
      if (!inverse[fnCode]) inverse[fnCode] = [];
      inverse[fnCode].push(capId);
    }
  }
  return inverse;
})();

/**
 * The maturity scale shared by both pages. Note ZT uses 'TRAD.' (with period)
 * historically; we normalize to CISA's 'TRADITIONAL' here.
 */
const LEVEL_INDEX = {
  'TRADITIONAL': 0,
  'TRAD.': 0,        // legacy ZT format
  'TRAD': 0,
  'INITIAL': 1,
  'INIT': 1,
  'ADVANCED': 2,
  'ADV': 2,
  'OPTIMAL': 3,
  'OPT': 3
};

const INDEX_TO_LEVEL = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];

export function normalizeLevel(level) {
  if (level == null) return null;
  const idx = LEVEL_INDEX[String(level).toUpperCase()];
  return typeof idx === 'number' ? INDEX_TO_LEVEL[idx] : null;
}

/**
 * Aggregate a list of ZT-capability levels into one CISA-function level.
 *
 * Strategy: take the MINIMUM across contributing capabilities. The reasoning
 * is that a CISA function is only as mature as its weakest contributing
 * piece — you can't claim ADVANCED segmentation if your ZTNA is still TRAD.
 * Returns null if no contributing capability has data.
 */
export function aggregateLevels(levels) {
  const valid = levels
    .map(normalizeLevel)
    .filter(l => l !== null)
    .map(l => LEVEL_INDEX[l]);
  if (valid.length === 0) return null;
  return INDEX_TO_LEVEL[Math.min(...valid)];
}

/**
 * Compare two levels — returns negative if a < b, 0 if equal, positive if a > b.
 * Useful for gap detection (target vs derived).
 */
export function compareLevels(a, b) {
  const ai = LEVEL_INDEX[normalizeLevel(a)] ?? 0;
  const bi = LEVEL_INDEX[normalizeLevel(b)] ?? 0;
  return ai - bi;
}

/**
 * True if a CISA function has any ZT mapping authored.
 * Cells without a mapping show "No tech-stack signal" rather than misleading data.
 */
export function isFunctionMapped(functionCode) {
  return Array.isArray(CISA_FUNCTION_TO_ZT[functionCode]) &&
    CISA_FUNCTION_TO_ZT[functionCode].length > 0;
}
